import { useMemo, type CSSProperties } from 'react';
import {
  MarkerType,
  type DefaultEdgeOptions,
  type Edge,
  type EdgeMarker,
  type Node,
} from '@xyflow/react';

/**
 * Resting connector colour: mostly the faint `--border` token, pulled toward
 * `--muted-foreground` only as far as it needs to stay legible against the dotted
 * canvas. Deliberately light — connectors are structure, not content, and the
 * nodes should carry the visual weight.
 */
export const WORKFLOW_EDGE_COLOR =
  'color-mix(in oklab, var(--border) 60%, var(--muted-foreground) 40%)';

/** A selected edge repaints primary so it's obvious which connector you clicked. */
const SELECTED_EDGE_COLOR = 'var(--primary)';

const EDGE_STROKE_WIDTH = 1.5;

/** Arrowhead geometry. Shared so the selected variant can only differ in colour. */
const EDGE_MARKER_SIZE = 18;

export const WORKFLOW_EDGE_STYLE: CSSProperties = {
  stroke: WORKFLOW_EDGE_COLOR,
  strokeWidth: EDGE_STROKE_WIDTH,
};

export const WORKFLOW_EDGE_MARKER: EdgeMarker = {
  type: MarkerType.Arrow,
  color: WORKFLOW_EDGE_COLOR,
  width: EDGE_MARKER_SIZE,
  height: EDGE_MARKER_SIZE,
};

/**
 * The resting arrowhead, faded to `opacity`.
 *
 * SVG markers are rendered once into a shared `<defs>` and keyed by
 * `getMarkerId`, which hashes every field *including* `color` — and they don't
 * inherit the referencing path's opacity. So fading a connector means pointing
 * it at its own pre-faded marker; CSS can't do it per edge.
 */
function fadedEdgeMarker(opacity: number): EdgeMarker {
  return {
    ...WORKFLOW_EDGE_MARKER,
    color: `color-mix(in oklab, ${WORKFLOW_EDGE_COLOR} ${
      opacity * 100
    }%, transparent)`,
  };
}

const DISABLED_EDGE_OPACITY = 0.45;

/** Distinct from `RUN_EDGE_DASH`, so a paused trigger can't read as a run. */
const DISABLED_EDGE_DASH = '4 4';

/**
 * The connector into a trigger that is switched off.
 *
 * Dashed and faded rather than absent: the trigger is still bound to this
 * workflow, just paused, and a card with no line into the graph reads as
 * detached. Dashes carry that on their own at any zoom, where colour alone
 * would not.
 */
export const WORKFLOW_DISABLED_EDGE_STYLE: CSSProperties = {
  ...WORKFLOW_EDGE_STYLE,
  strokeDasharray: DISABLED_EDGE_DASH,
  opacity: DISABLED_EDGE_OPACITY,
};

export const WORKFLOW_DISABLED_EDGE_MARKER: EdgeMarker = fadedEdgeMarker(
  DISABLED_EDGE_OPACITY
);

/**
 * `smoothstep`, not `step`: identical orthogonal routing, but the corners are
 * filleted with React Flow's default 5px radius so a bend reads softly instead of
 * as a hard right angle. (`step` is literally smoothstep with `borderRadius: 0`.)
 * A larger radius would need `pathOptions`, which `DefaultEdgeOptions` can't carry
 * — it's typed against the base `Edge`, where `pathOptions` only exists on the
 * per-variant `SmoothStepEdge`.
 *
 * Shared by the editor canvas, the run graph, and the run-workflow drawer preview
 * so the three can't drift apart.
 *
 * Exported separately because the graph builders stamp `type` onto each edge, and
 * a per-edge `type` beats `defaultEdgeOptions.type` — setting it only in the
 * options below would have no effect at all. Every builder must use this.
 */
export const WORKFLOW_EDGE_TYPE = 'smoothstep' as const;

export const WORKFLOW_EDGE_OPTIONS: DefaultEdgeOptions = {
  type: WORKFLOW_EDGE_TYPE,
  style: WORKFLOW_EDGE_STYLE,
  markerEnd: WORKFLOW_EDGE_MARKER,
};

const SELECTED_EDGE_MARKER: EdgeMarker = {
  ...WORKFLOW_EDGE_MARKER,
  color: SELECTED_EDGE_COLOR,
};

/**
 * How far everything outside the selection fades once a node is picked.
 *
 * Far enough that the lit path is unmistakable, not so far that the rest of the
 * graph stops being readable — the dimmed steps are still context you navigate
 * by, and they stay clickable.
 */
const DIMMED_OPACITY = 0.7;

/** The resting arrowhead, faded to match a dimmed connector. */
const DIMMED_EDGE_MARKER: EdgeMarker = fadedEdgeMarker(DIMMED_OPACITY);

/** Separator for the selection key — node ids are uuids or `trigger:<uuid>`. */
const SELECTION_KEY_SEPARATOR = '\n';

/**
 * Ids of the selected nodes, as a set whose *identity* holds steady for as long as
 * the selection does.
 *
 * That stability is the whole point. Dragging replaces every node object each
 * frame, so memoising on `nodes` directly would rebuild the highlighted-edge array
 * on every frame of every drag. Keying on a joined id string instead means only an
 * actual selection change invalidates it; the per-render scan below is O(nodes) on
 * primitives, which is nothing next to allocating a fresh edge array.
 */
export function useSelectedNodeIds(nodes: Node[]): ReadonlySet<string> {
  const key = nodes
    .filter((node) => node.selected === true)
    .map((node) => node.id)
    .sort()
    .join(SELECTION_KEY_SEPARATOR);

  return useMemo(
    () => new Set(key === '' ? [] : key.split(SELECTION_KEY_SEPARATOR)),
    [key]
  );
}

/**
 * Repaint highlighted edges, for rendering only.
 *
 * An edge highlights when it is selected itself, *or* when either end sits on a
 * selected node — so clicking a card lights up everything flowing in and out of
 * it, which is what makes a step's place in a busy graph readable at a glance.
 *
 * Two things force this to be a per-edge derivation rather than CSS:
 *
 * 1. React Flow's own `--xy-edge-stroke-selected` only reaches the line, only
 *    responds to the edge's own selection (never the adjacent node's), and loses
 *    to our inline `style.stroke` anyway (inline beats a stylesheet).
 * 2. Arrowheads are SVG markers rendered once into a shared `<defs>` and keyed by
 *    `getMarkerId`, which hashes every field *including* `color`. A differently
 *    coloured arrowhead is therefore a different marker, not something CSS can
 *    restyle per edge — so a highlighted edge has to point at its own.
 *
 * The style is rebuilt from `WORKFLOW_EDGE_STYLE` because React Flow merges
 * `defaultEdgeOptions` at the edge level: setting `style` here replaces the
 * default wholesale, which would silently drop `strokeWidth`. Any style already on
 * the edge still wins, so an edge highlighted mid-run keeps its marching dashes.
 *
 * Derive this at render time and never write it back into edge state, or the
 * highlight would outlive the selection.
 */
export function withSelectedEdgeHighlight(
  edges: Edge[],
  selectedNodeIds: ReadonlySet<string>
): Edge[] {
  return edges.map((edge) => {
    if (
      edge.selected === true ||
      selectedNodeIds.has(edge.source) ||
      selectedNodeIds.has(edge.target)
    ) {
      return {
        ...edge,
        style: {
          ...WORKFLOW_EDGE_STYLE,
          ...edge.style,
          stroke: SELECTED_EDGE_COLOR,
        },
        markerEnd: SELECTED_EDGE_MARKER,
      };
    }
    // The other half of the same idea: with a selection on screen, a connector
    // that has nothing to do with it recedes. Untouched when nothing is selected,
    // so the resting graph allocates no new edge objects.
    if (selectedNodeIds.size === 0) {
      return edge;
    }
    return {
      ...edge,
      style: {
        ...WORKFLOW_EDGE_STYLE,
        ...edge.style,
        opacity: DIMMED_OPACITY,
      },
      markerEnd: DIMMED_EDGE_MARKER,
    };
  });
}

/**
 * The nodes that stay lit while something is selected: the selection itself, and
 * whatever it is directly wired to.
 *
 * Neighbours rather than the selection alone, because the thing a graph can say
 * and an inspector cannot is *where a step sits* — what feeds it and what it
 * feeds. Lighting one card in isolation throws that away.
 *
 * Returns the selection set unchanged when nothing is selected, so callers can
 * treat "empty" as "nothing dims" without a second flag.
 */
export function litNodeIds(
  edges: Edge[],
  selectedNodeIds: ReadonlySet<string>
): ReadonlySet<string> {
  if (selectedNodeIds.size === 0) {
    return selectedNodeIds;
  }
  const lit = new Set(selectedNodeIds);
  for (const edge of edges) {
    if (selectedNodeIds.has(edge.source)) {
      lit.add(edge.target);
    }
    if (selectedNodeIds.has(edge.target)) {
      lit.add(edge.source);
    }
  }
  return lit;
}

/**
 * Fades the nodes outside `litIds`.
 *
 * Derived at render time and never written back into node state, for the same
 * reason as the edge highlight below: a stored opacity would outlive the
 * selection that justified it. Returns the input array untouched when nothing is
 * selected, so the no-selection case allocates nothing — this runs on every frame
 * of a drag.
 */
export function withUnselectedNodesDimmed(
  nodes: Node[],
  litIds: ReadonlySet<string>
): Node[] {
  if (litIds.size === 0) {
    return nodes;
  }
  return nodes.map((node) =>
    litIds.has(node.id)
      ? node
      : { ...node, style: { ...node.style, opacity: DIMMED_OPACITY } }
  );
}
